import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Hexagon, ArrowLeft, ArrowRight, Loader2, Dumbbell, ExternalLink, Target,
  Lightbulb, RefreshCw, Calendar, ChevronRight,
} from "lucide-react";
import Orb from "@/components/Orb";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const QUESTIONS = [
  { key: "days_per_week", q: "How many days per week can you train?", type: "days",
    options: [{ v: 2, l: "2 days" }, { v: 3, l: "3 days" }, { v: 4, l: "4 days" }, { v: 5, l: "5 days" }, { v: 6, l: "6 days" }] },
  { key: "experience", q: "How long have you trained calisthenics?",
    options: ["Complete beginner", "Less than 6 months", "6 months – 2 years", "2+ years"] },
  { key: "goal", q: "What's your main goal?",
    options: ["Build raw strength", "Learn skills (planche, levers, etc.)", "Build muscle", "Balanced strength & skill"] },
  { key: "push", q: "PUSH — how many consecutive push-ups can you do?",
    options: ["I can't do a full push-up yet", "1–10 push-ups", "11–25 push-ups", "25+ / I can do pike or handstand push-ups", "I train planche work"] },
  { key: "pull", q: "PULL — where are you with pull-ups?",
    options: ["I can't hang from a bar yet", "I can hang but no pull-ups", "1–5 pull-ups", "6–12 pull-ups", "12+ / muscle-ups / front lever work"] },
  { key: "handstand", q: "HANDSTAND — how's your overhead / balance work?",
    options: ["No handstand practice", "Wall handstand / crow pose", "Freestanding handstand", "Handstand push-ups"] },
  { key: "legs", q: "LEGS — where are your single-leg strength?",
    options: ["Bodyweight squats only", "20+ bodyweight squats", "Assisted / partial pistol squats", "Full pistol squats", "Shrimp / dragon pistol squats"] },
  { key: "core", q: "CORE — what can you hold?",
    options: ["Plank under 30s", "Plank 30–60s / sit-ups", "L-sit tuck / hollow holds", "Full L-sit", "Dragon flag / front lever"] },
  { key: "equipment", q: "What equipment do you have? (choose any)", type: "multi",
    options: ["Pull-up bar", "Parallettes / dip bars", "Gymnastic rings", "Resistance bands", "None (floor only)"] },
];

export default function Training() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [plan, setPlan] = useState(null);
  const [checking, setChecking] = useState(true);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ days_per_week: 3, equipment: [] });
  const [generating, setGenerating] = useState(false);
  const [quizMode, setQuizMode] = useState(false);
  const [genError, setGenError] = useState("");

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/training/plan");
      if (data.plan) setPlan(data.plan);
      else setQuizMode(true);
    } catch { setQuizMode(true); } finally { setChecking(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const current = QUESTIONS[step];

  const answer = (val) => {
    if (current.type === "multi") {
      const list = answers.equipment || [];
      const next = list.includes(val) ? list.filter((x) => x !== val) : [...list, val];
      setAnswers({ ...answers, equipment: next });
      return;
    }
    setAnswers({ ...answers, [current.key]: val });
    if (step < QUESTIONS.length - 1) setStep(step + 1);
  };

  const generate = async () => {
    setGenerating(true);
    setGenError("");
    try {
      const { data } = await api.post("/training/generate", answers);
      setPlan(data.plan);
      setQuizMode(false);
    } catch {
      setGenError("The System couldn't forge your protocol just now. Please try again.");
    } finally { setGenerating(false); }
  };

  const retake = () => { setPlan(null); setStep(0); setAnswers({ days_per_week: 3, equipment: [] }); setQuizMode(true); };

  if (checking) {
    return <div className="h-screen grid-bg flex items-center justify-center"><Loader2 className="animate-spin text-cyan-400" size={30} /></div>;
  }

  if (generating) {
    return (
      <div className="h-screen grid-bg flex flex-col items-center justify-center text-center px-6">
        <Orb state="thinking" size={180} />
        <p className="mono-label mt-10">FORGING PROTOCOL</p>
        <h2 className="font-display text-3xl font-bold mt-3">Building your training path…</h2>
        <p className="text-slate-400 mt-2">Mapping your level onto the WINGS skill tree.</p>
        <Loader2 className="animate-spin text-cyan-400 mt-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grid-bg">
      <div className="fixed inset-0 glow-radial pointer-events-none" />
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4 border-b border-blue-900/30 backdrop-blur">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-cyan-300" data-testid="back-btn"><ArrowLeft size={18} /></button>
          <div className="flex items-center gap-2">
            <Dumbbell className="text-cyan-400" size={18} />
            <div>
              <p className="font-display font-semibold leading-tight">Training Protocol</p>
              <p className="mono-label">POWERED BY WINGS SKILL TREE</p>
            </div>
          </div>
        </div>
        {plan && !quizMode && (
          <button data-testid="retake-btn" onClick={retake} className="mono-label flex items-center gap-1.5 border border-blue-900/40 px-3 py-1.5 rounded-full hover:bg-blue-500/10 transition-all">
            <RefreshCw size={13} /> Reassess
          </button>
        )}
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {quizMode ? (
          <>
          {genError && <p data-testid="gen-error" className="max-w-2xl mx-auto mb-4 text-red-400 text-sm border border-red-500/30 bg-red-500/5 rounded-lg px-4 py-3">{genError}</p>}
          <QuizStep current={current} step={step} answers={answers} answer={answer}
            onBack={() => setStep(Math.max(0, step - 1))}
            onNext={() => { if (current.type === "multi" || current.key === "days_per_week") { if (step < QUESTIONS.length - 1) setStep(step + 1); else generate(); } else generate(); }}
            isLast={step === QUESTIONS.length - 1} generate={generate} />
          </>
        ) : plan ? (
          <PlanView plan={plan} />
        ) : null}
      </div>
    </div>
  );
}

function QuizStep({ current, step, answers, answer, onBack, onNext, isLast }) {
  const total = QUESTIONS.length;
  const selected = answers[current.key];
  const isMulti = current.type === "multi";
  const isDays = current.key === "days_per_week";
  return (
    <div className="max-w-2xl mx-auto" data-testid="assessment-quiz">
      <div className="flex items-center gap-3 mb-8">
        <Hexagon className="text-cyan-400" size={16} />
        <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
        </div>
        <span className="mono-label">{step + 1}/{total}</span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
          <p className="mono-label">ASSESSMENT</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2 mb-8">{current.q}</h2>
          <div className="space-y-3">
            {current.options.map((opt) => {
              const val = isDays ? opt.v : opt;
              const label = isDays ? opt.l : opt;
              const active = isMulti ? (answers.equipment || []).includes(val) : selected === val;
              return (
                <button key={label} data-testid={`option-${String(val).slice(0, 20)}`} onClick={() => answer(val)}
                  className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center justify-between ${active ? "border-cyan-400/60 bg-cyan-400/10 text-slate-50" : "border-blue-900/40 text-slate-300 hover:bg-white/5 hover:border-blue-500/40"}`}>
                  <span>{label}</span>
                  {active && <ChevronRight size={16} className="text-cyan-400" />}
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-10">
        <button data-testid="quiz-back" onClick={onBack} disabled={step === 0}
          className="flex items-center gap-2 text-slate-400 disabled:opacity-30 hover:text-cyan-300 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        {(isMulti || isDays) && (
          <button data-testid="quiz-next" onClick={onNext}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-full font-medium transition-all hover:shadow-[0_0_22px_rgba(59,130,246,0.5)]">
            {isLast ? "Forge my protocol" : "Next"} <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function WingsChip({ href, label }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`wings-link-${label}`}
      className="flex items-center gap-1.5 text-xs border border-blue-900/40 rounded-full px-3 py-1.5 text-cyan-300 hover:bg-cyan-400/10 transition-all">
      {label} <ExternalLink size={11} />
    </a>
  );
}

function PlanView({ plan }) {
  const wl = plan.wings_links || {};
  return (
    <div className="space-y-8" data-testid="training-plan">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 sm:p-8">
        <p className="mono-label">YOUR FITNESS LEVEL</p>
        <h1 className="font-display text-4xl font-extrabold text-cyan-300 mt-1" data-testid="fitness-level">{plan.fitness_level}</h1>
        <p className="text-slate-300 mt-3 max-w-3xl leading-relaxed">{plan.summary}</p>
        <div className="flex flex-wrap gap-2 mt-5">
          <WingsChip href={wl.skilltree} label="Skill Tree" />
          <WingsChip href={wl.tutorials} label="Tutorials" />
          <WingsChip href={wl.dashboard} label="Track on Dashboard" />
          <WingsChip href={wl.home} label="WINGS Home" />
        </div>
      </motion.div>

      {/* Placement */}
      <section data-testid="placement-section">
        <SectionTitle icon={Target} label="WHERE YOU STAND" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(plan.placement || []).map((p, i) => (
            <div key={i} className="glass rounded-xl p-4" data-testid={`placement-${i}`}>
              <p className="mono-label">{p.category}</p>
              <p className="font-display text-lg font-semibold text-slate-100 mt-1">{p.current}</p>
              <p className="text-sm text-cyan-300 mt-1 flex items-center gap-1"><ArrowRight size={12} /> {p.target}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Weekly schedule */}
      <section data-testid="schedule-section">
        <SectionTitle icon={Calendar} label="WEEKLY PROTOCOL" />
        <div className="grid md:grid-cols-2 gap-4">
          {(plan.schedule || []).map((day, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="glass rounded-xl p-5" data-testid={`day-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <p className="font-display font-bold text-lg">{day.day}</p>
                <span className="mono-label">{day.focus}</span>
              </div>
              <div className="space-y-3">
                {(day.exercises || []).map((ex, j) => (
                  <div key={j} className="border border-blue-900/30 rounded-lg p-3" data-testid={`exercise-${i}-${j}`}>
                    <div className="flex items-center justify-between gap-2">
                      <a href={ex.wings} target="_blank" rel="noopener noreferrer"
                        className="font-display font-semibold text-cyan-200 hover:underline flex items-center gap-1">
                        {ex.skill} <ExternalLink size={11} className="opacity-60" />
                      </a>
                      <span className="mono-label whitespace-nowrap">{ex.sets} × {ex.reps}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{ex.category}</p>
                    {ex.note && <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{ex.note}</p>}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Progression targets */}
      <section data-testid="targets-section">
        <SectionTitle icon={ChevronRight} label="NEXT SKILLS TO UNLOCK" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(plan.progression_targets || []).map((t, i) => (
            <div key={i} className="rounded-xl p-4 border border-cyan-400/30 bg-cyan-400/5" data-testid={`target-${i}`}>
              <p className="font-display font-semibold text-cyan-200">{t.skill}</p>
              <p className="mono-label mt-0.5">{t.category}</p>
              <p className="text-sm text-slate-400 mt-2">{t.why}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tips */}
      <section className="pb-10" data-testid="tips-section">
        <SectionTitle icon={Lightbulb} label="ARCHON'S DIRECTIVES" />
        <div className="glass rounded-xl p-5 space-y-3">
          {(plan.tips || []).map((t, i) => (
            <div key={i} className="flex gap-3 text-slate-300" data-testid={`tip-${i}`}>
              <span className="mono-label text-cyan-400 shrink-0 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm leading-relaxed">{t}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-600 mt-4">
          Skill data & tutorials from <a href={wl.home} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">WINGS</a>.
          Report bugs to WINGS via their <a href={wl.feedback} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">feedback form</a>.
        </p>
      </section>
    </div>
  );
}

function SectionTitle({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <Icon className="text-cyan-400" size={18} />
      <h2 className="font-display text-lg font-semibold tracking-wide">{label}</h2>
    </div>
  );
}
